// SNIGJ - Accounting & Payments Management Page
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import PrintableDocument from '../components/documents/PrintableDocument';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  DollarSign, CreditCard, Building2, TrendingUp, Receipt, Printer, Download,
  Search, Filter, Eye, CheckCircle, Clock, AlertTriangle, Calendar, FileText,
  PieChart, BarChart3, Wallet, BanknoteIcon, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DRC_PROVINCES } from '../lib/drc-data';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AccountingPage = () => {
  const { language, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [fees, setFees] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    jurisdiction: '',
    date_from: '',
    date_to: '',
    fee_type: ''
  });
  const [showBankModal, setShowBankModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newBankAccount, setNewBankAccount] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    jurisdiction: '',
    swift_code: '',
    currency: 'USD'
  });
  const printRef = useRef();

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, paymentsRes, feesRes, banksRes] = await Promise.all([
        api.get('/admin/accounting/stats', { params: filters }),
        api.get('/admin/accounting/payments', { params: { ...filters, limit: 100 } }),
        api.get('/admin/accounting/fees', { params: { ...filters, limit: 100 } }),
        api.get('/admin/accounting/bank-accounts')
      ]);
      setStats(statsRes.data);
      setPayments(paymentsRes.data.payments || []);
      setFees(feesRes.data.fees || []);
      setBankAccounts(banksRes.data.accounts || []);
    } catch (error) {
      console.error('Failed to load accounting data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBankAccount = async () => {
    try {
      await api.post('/admin/accounting/bank-accounts', newBankAccount);
      toast.success(language === 'fr' ? 'Compte bancaire ajouté' : 'Bank account added');
      setShowBankModal(false);
      loadData();
      setNewBankAccount({ bank_name: '', account_number: '', account_name: '', jurisdiction: '', swift_code: '', currency: 'USD' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handlePrintInvoice = () => {
    const printContent = printRef.current;
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${language === 'fr' ? 'Note de Perception' : 'Invoice'} - ${selectedItem?.fee_id}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; }
              .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 20px; }
              .header h1 { margin: 0; font-size: 18px; }
              .header h2 { margin: 5px 0; font-size: 14px; font-weight: normal; }
              .details { margin: 20px 0; }
              .details table { width: 100%; border-collapse: collapse; }
              .details td { padding: 8px; border-bottom: 1px solid #ddd; }
              .details td:first-child { font-weight: bold; width: 40%; }
              .amount { font-size: 24px; font-weight: bold; text-align: center; margin: 30px 0; padding: 20px; border: 2px solid #000; }
              .footer { margin-top: 40px; text-align: center; font-size: 12px; }
              .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-weight: bold; }
              .status.paid { background: #d4edda; color: #155724; }
              .status.pending { background: #fff3cd; color: #856404; }
              @media print { body { padding: 20px; } }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const openInvoice = (item) => {
    setSelectedItem(item);
    setShowInvoiceModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PAID: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-green-100 text-green-800',
      OVERDUE: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      FAILED: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const feeTypes = ['COURT_FEE', 'FINE', 'PENALTY', 'ADMINISTRATIVE_FEE'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="accounting-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <DollarSign className="w-8 h-8" />
              {language === 'fr' ? 'Comptabilité & Paiements' : 'Accounting & Payments'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gestion des recettes et notes de perception' : 'Revenue and fee management'}
            </p>
          </div>
          <Dialog open={showBankModal} onOpenChange={setShowBankModal}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                {language === 'fr' ? 'Ajouter compte bancaire' : 'Add Bank Account'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{language === 'fr' ? 'Nouveau compte bancaire' : 'New Bank Account'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom de la banque' : 'Bank Name'}</Label>
                  <Input value={newBankAccount.bank_name} onChange={(e) => setNewBankAccount({...newBankAccount, bank_name: e.target.value})} placeholder="RAWBANK, BIAC, etc." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'N° de compte' : 'Account Number'}</Label>
                    <Input value={newBankAccount.account_number} onChange={(e) => setNewBankAccount({...newBankAccount, account_number: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>SWIFT/BIC</Label>
                    <Input value={newBankAccount.swift_code} onChange={(e) => setNewBankAccount({...newBankAccount, swift_code: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Intitulé du compte' : 'Account Name'}</Label>
                  <Input value={newBankAccount.account_name} onChange={(e) => setNewBankAccount({...newBankAccount, account_name: e.target.value})} placeholder="MINISTERE DE LA JUSTICE - TGI KINSHASA" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Juridiction</Label>
                    <Select value={newBankAccount.jurisdiction} onValueChange={(v) => setNewBankAccount({...newBankAccount, jurisdiction: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {DRC_PROVINCES.map((p) => (<SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Devise' : 'Currency'}</Label>
                    <Select value={newBankAccount.currency} onValueChange={(v) => setNewBankAccount({...newBankAccount, currency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="CDF">CDF (FC)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleAddBankAccount} className="w-full">{language === 'fr' ? 'Enregistrer' : 'Save'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_collected?.toLocaleString() || 0} USD</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total perçu' : 'Total Collected'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pending_amount?.toLocaleString() || 0} USD</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'En attente' : 'Pending'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_fees || 0}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Notes émises' : 'Fees Issued'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.overdue_count || 0}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'En retard' : 'Overdue'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Select value={filters.jurisdiction || "ALL"} onValueChange={(v) => setFilters({...filters, jurisdiction: v === "ALL" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Juridiction" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Toutes' : 'All'}</SelectItem>
                  {DRC_PROVINCES.map((p) => (<SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.status || "ALL"} onValueChange={(v) => setFilters({...filters, status: v === "ALL" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="PENDING">{language === 'fr' ? 'En attente' : 'Pending'}</SelectItem>
                  <SelectItem value="PAID">{language === 'fr' ? 'Payé' : 'Paid'}</SelectItem>
                  <SelectItem value="OVERDUE">{language === 'fr' ? 'En retard' : 'Overdue'}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.fee_type || "ALL"} onValueChange={(v) => setFilters({...filters, fee_type: v === "ALL" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  {feeTypes.map((t) => (<SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from: e.target.value})} placeholder="Du" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to: e.target.value})} placeholder="Au" />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="fees" className="space-y-4">
          <TabsList>
            <TabsTrigger value="fees" className="gap-2"><Receipt className="w-4 h-4" />{language === 'fr' ? 'Notes de perception' : 'Fees'}</TabsTrigger>
            <TabsTrigger value="payments" className="gap-2"><CreditCard className="w-4 h-4" />{language === 'fr' ? 'Paiements' : 'Payments'}</TabsTrigger>
            <TabsTrigger value="stats" className="gap-2"><BarChart3 className="w-4 h-4" />{language === 'fr' ? 'Statistiques' : 'Statistics'}</TabsTrigger>
            <TabsTrigger value="banks" className="gap-2"><Building2 className="w-4 h-4" />{language === 'fr' ? 'Comptes bancaires' : 'Bank Accounts'}</TabsTrigger>
          </TabsList>

          {/* Fees Tab */}
          <TabsContent value="fees">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr className="bg-muted/50">
                        <th>N° Note</th>
                        <th>{language === 'fr' ? 'Citoyen' : 'Citizen'}</th>
                        <th>Type</th>
                        <th>{language === 'fr' ? 'Montant' : 'Amount'}</th>
                        <th>{language === 'fr' ? 'Échéance' : 'Due Date'}</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        Array.from({length: 5}).map((_, i) => (<tr key={i}>{Array.from({length: 7}).map((_, j) => (<td key={j}><div className="h-4 skeleton" /></td>))}</tr>))
                      ) : fees.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">{language === 'fr' ? 'Aucune note' : 'No fees'}</td></tr>
                      ) : (
                        fees.map((fee) => (
                          <tr key={fee.fee_id}>
                            <td className="font-mono text-sm">{fee.fee_id?.slice(-8)}</td>
                            <td>{fee.citizen_name || fee.citizen_id?.slice(-8)}</td>
                            <td>{fee.fee_type?.replace(/_/g, ' ')}</td>
                            <td className="font-bold">{fee.amount?.toFixed(2)} {fee.currency}</td>
                            <td>{fee.due_date}</td>
                            <td><Badge className={getStatusBadge(fee.status)}>{fee.status}</Badge></td>
                            <td>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openInvoice(fee)}><Eye className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => { setSelectedItem(fee); setShowInvoiceModal(true); setTimeout(handlePrintInvoice, 100); }}><Printer className="w-4 h-4" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr className="bg-muted/50">
                        <th>Transaction</th>
                        <th>{language === 'fr' ? 'Note liée' : 'Related Fee'}</th>
                        <th>{language === 'fr' ? 'Payeur' : 'Payer'}</th>
                        <th>{language === 'fr' ? 'Montant' : 'Amount'}</th>
                        <th>{language === 'fr' ? 'Méthode' : 'Method'}</th>
                        <th>Date</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        Array.from({length: 5}).map((_, i) => (<tr key={i}>{Array.from({length: 8}).map((_, j) => (<td key={j}><div className="h-4 skeleton" /></td>))}</tr>))
                      ) : payments.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">{language === 'fr' ? 'Aucun paiement' : 'No payments'}</td></tr>
                      ) : (
                        payments.map((payment) => (
                          <tr key={payment.transaction_id}>
                            <td className="font-mono text-sm">{payment.transaction_id?.slice(-8)}</td>
                            <td className="font-mono text-sm">{payment.fee_id?.slice(-8) || '—'}</td>
                            <td>{payment.payer_name || payment.payer_id?.slice(-8)}</td>
                            <td className="font-bold text-green-600">{payment.amount?.toFixed(2)} {payment.currency}</td>
                            <td>
                              {payment.payment_method === 'card' && <CreditCard className="w-4 h-4 inline mr-1" />}
                              {payment.payment_method === 'mobile_money' && <Wallet className="w-4 h-4 inline mr-1" />}
                              {payment.payment_method === 'bank_transfer' && <Building2 className="w-4 h-4 inline mr-1" />}
                              {payment.payment_method}
                            </td>
                            <td>{new Date(payment.paid_at || payment.created_at).toLocaleDateString()}</td>
                            <td><Badge className={getStatusBadge(payment.status)}>{payment.status}</Badge></td>
                            <td>
                              <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Recettes par juridiction' : 'Revenue by Jurisdiction'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats?.by_jurisdiction || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="jurisdiction" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="amount" fill="#0088FE" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Répartition par type' : 'Distribution by Type'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie data={stats?.by_type || []} cx="50%" cy="50%" outerRadius={100} dataKey="amount" label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}>
                          {(stats?.by_type || []).map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bank Accounts Tab */}
          <TabsContent value="banks">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bankAccounts.map((account) => (
                <Card key={account.account_id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <Badge variant="outline">{account.currency}</Badge>
                    </div>
                    <h3 className="font-bold">{account.bank_name}</h3>
                    <p className="text-sm text-muted-foreground font-mono">{account.account_number}</p>
                    <p className="text-sm mt-2">{account.account_name}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                      <span>SWIFT: {account.swift_code || '—'}</span>
                      <span>•</span>
                      <span>{account.jurisdiction}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {bankAccounts.length === 0 && (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  {language === 'fr' ? 'Aucun compte bancaire enregistré' : 'No bank accounts registered'}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invoice Modal with Logo and QR Code */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{language === 'fr' ? 'Note de Perception' : 'Fee Invoice'}</span>
              <Button variant="outline" size="sm" onClick={() => { const btn = document.querySelector('[data-print-trigger]'); if (btn) btn.click(); }} className="gap-2">
                <Printer className="w-4 h-4" />
                {language === 'fr' ? 'Imprimer' : 'Print'}
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <PrintableDocument
              ref={printRef}
              documentType="PERCEPTION_NOTE"
              documentId={selectedItem.fee_id}
              data={selectedItem}
              language={language}
            />
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AccountingPage;
